import { z } from 'zod';
import { idSchema } from './common.js';
/**
 * Reading Message Hub's messages on a project or a contact (TS-1897, step 2 of TS-1891; plan
 * `.claude/plans/messages.md`). The messages are the copy the message feed keeps (TS-1892,
 * `text_messages` / `email_messages`); they are not linked to records. A record's messages are
 * found when read, by its contacts' details:
 *
 * - **Texts** by phone: a contact's `phone` and `alt_phone`, digits only, against the client's
 *   number (E.164 digits from Twilio/Quo), with Message Hub's rule (`samePhone`).
 * - **Emails** by address: a contact's email, trimmed and lower-cased, among the email's
 *   `client_addresses`.
 *
 * Texts are read as conversations (one per client number and company line), then a
 * conversation's messages; emails as one list. All newest first.
 */
/**
 * Whether a number typed on a contact is the client's number from Twilio/Quo, both digits only.
 * Message Hub's rule (`lib/crm-match.js` `samePhone`), kept identical so both show the same
 * messages: equal, or the typed number is the client's without its country code (at most 3
 * digits left out, at least 10 kept). So a number typed with its country code matches only that
 * number; one typed without it (`(305) 555-0100`) matches it under ANY country code
 * (`+1…`, `+52…`) — the typed number cannot say which. A number that only shares the last digits
 * (fewer than 10, or more than 3 extra) never matches.
 */
export function samePhone(clientDigits, contactDigits) {
    if (!clientDigits || !contactDigits)
        return false;
    if (clientDigits === contactDigits)
        return true;
    return (contactDigits.length >= 10 &&
        clientDigits.length > contactDigits.length &&
        clientDigits.length - contactDigits.length <= 3 &&
        clientDigits.endsWith(contactDigits));
}
/** The candidate key the database matches on before `samePhone` confirms: the last 10 digits. */
export const phoneMatchKey = (digits) => digits.slice(-10);
/** A conversation: one client number with one company line. `key` is `<client>:<line>`, digits. */
export const conversationKeySchema = z
    .string()
    .regex(/^\d{1,20}:\d{0,20}$/, 'a conversation key is <client digits>:<line digits>');
export function conversationKey(clientDigits, ourNumber) {
    return `${clientDigits}:${(ourNumber ?? '').replace(/\D/g, '')}`;
}
/** Which of the record's contacts a message matched (a project has several). */
const matchedContactsSchema = z.array(idSchema);
export const messageConversationSchema = z.object({
    key: z.string(),
    clientNumber: z.string().nullable(),
    ourNumber: z.string().nullable(),
    ourNumberName: z.string().nullable(),
    /** Every channel seen in it (`sms`, `mms`, `whatsapp`, `rcs`…). */
    channels: z.array(z.string()),
    messageCount: z.number().int(),
    lastMessageAt: z.iso.datetime({ offset: true }).nullable(),
    /** The newest message's first 160 characters. */
    lastMessagePreview: z.string().nullable(),
    lastDirection: z.enum(['inbound', 'outbound']).nullable(),
    contactIds: matchedContactsSchema,
    /**
     * Whether the reader may reply in it from the CRM (TS-1904): replies are on and set up, and the
     * reader holds `message.send` over the record or one of the contacts on this number.
     */
    canReply: z.boolean(),
});
export const textMessageViewSchema = z.object({
    id: idSchema,
    provider: z.string(),
    channel: z.string(),
    direction: z.enum(['inbound', 'outbound']),
    clientNumber: z.string().nullable(),
    ourNumber: z.string().nullable(),
    ourNumberName: z.string().nullable(),
    senderName: z.string().nullable(),
    body: z.string().nullable(),
    status: z.string().nullable(),
    errorCode: z.number().int().nullable(),
    errorMessage: z.string().nullable(),
    numMedia: z.number().int().nullable(),
    sentAt: z.iso.datetime({ offset: true }).nullable(),
    statusUpdatedAt: z.iso.datetime({ offset: true }).nullable(),
    contactIds: matchedContactsSchema,
    /**
     * A reply sent from the CRM (TS-1904) that Message Hub has not copied back yet, or one Twilio
     * refused (`status` `failed`, `errorMessage` says why). It is replaced by Message Hub's copy
     * within a few minutes of being sent.
     */
    sentFromCrm: z.boolean(),
});
const addressSchema = z.object({ address: z.string().nullable(), name: z.string().nullable() });
/**
 * An email as the tab shows it. BCC is left out: it lists who was copied without the others
 * knowing, and the tab is open to everyone who can read the client (assumption, plan step 3).
 */
export const emailMessageViewSchema = z.object({
    id: idSchema,
    subject: z.string().nullable(),
    fromAddress: z.string().nullable(),
    fromName: z.string().nullable(),
    to: z.array(addressSchema),
    cc: z.array(addressSchema),
    direction: z.enum(['inbound', 'outbound']),
    bodyText: z.string().nullable(),
    bodyTruncated: z.boolean(),
    snippet: z.string().nullable(),
    attachmentCount: z.number().int(),
    threadRef: z.string().nullable(),
    sentAt: z.iso.datetime({ offset: true }).nullable(),
    contactIds: matchedContactsSchema,
});
export const MESSAGES_PAGE_MAX = 100;
export const messagesPageQuerySchema = z.object({
    cursor: z.string().min(1).max(500).optional(),
    limit: z.coerce.number().int().min(1).max(MESSAGES_PAGE_MAX).default(50),
});
export const conversationMessagesQuerySchema = messagesPageQuerySchema.extend({
    conversation: conversationKeySchema,
});
const page = (item) => z.object({ items: z.array(item), nextCursor: z.string().nullable() });
export const textMessagePageSchema = page(textMessageViewSchema);
export const emailMessagePageSchema = page(emailMessageViewSchema);
export const messageConversationListSchema = z.object({
    items: z.array(messageConversationSchema),
    /** Contacts of the record that have no phone (their texts cannot be found) / no email. */
    contactsWithoutPhone: z.number().int(),
    contactsWithoutEmail: z.number().int(),
    /** Why the reader cannot reply in any conversation, when that is so for all of them; else null. */
    replyUnavailable: z.string().nullable(),
});
/** The longest reply a person may type; the sender's name line is added on top (TS-1904). */
export const TEXT_REPLY_MAX = 1_400;
/**
 * Replying to a text (TS-1904): `POST /{projects|contacts}/{id}/messages/texts`. Sent through the
 * company's Twilio Messaging Service with the sender's name on the first line.
 */
export const textReplySchema = z.object({
    conversation: conversationKeySchema,
    body: z
        .string()
        .transform((s) => s.replace(/\r\n?/g, '\n').trim())
        .pipe(z.string().min(1, 'type a message').max(TEXT_REPLY_MAX)),
});
/** The text as the client receives it: the sender's first and last name, then the reply. */
export function replyBody(senderName, body) {
    return `${senderName.trim()}\n${body}`;
}
/** The reply as sent, or as refused (`message.status` `failed`, `message.errorMessage` why). */
export const textReplyResultSchema = z.object({ message: textMessageViewSchema });
//# sourceMappingURL=messages.js.map